import React, { useState, useRef } from 'react';
import { feedbackAPI } from '../api';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';

const ResidentFeedbackForm = ({ homeId, homeDetails, onBack }) => {
  const [formData, setFormData] = useState({
    isAnonymous: false,
    residentFullName: '',
    guardianParentName: '',
    feedbackType: '',
    experienceRating: '',
    serviceArea: '',
    feedbackDetails: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const signaturePad = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const clearSignature = () => {
    signaturePad.current.clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (signaturePad.current.isEmpty()) {
      toast.error('Please provide your digital signature');
      return;
    }

    const signature = signaturePad.current.toDataURL();

    try {
      await feedbackAPI.submitFeedback({
        ...formData,
        homeId,
        digitalSignature: signature,
        consentGiven: true
      });
      setSubmitted(true);
      toast.success('Feedback submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit feedback');
    }
  };

  if (submitted) {
    return (
      <div className="log-form-container">
        <div className="log-form-card">
          <div className="success-message">
            <div className="success-icon"><FaCheckCircle /></div>
            <h2 className="success-title">Thank You!</h2>
            <p className="success-text">
              Your feedback has been submitted successfully. We value your input and will use it to improve our services.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="log-form-container">
      <div className="log-form-card">
        <div className="log-form-header">
          <img src="/littleFeetlogo.webp" alt="Little Feet Homes" className="log-form-logo" />
          <h1 className="log-form-title">Resident Feedback</h1>
          <p className="log-form-subtitle">{homeDetails.title}</p>
          <button onClick={onBack} className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }}>
            <FaArrowLeft /> Back
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Anonymous Toggle */}
          <div className="anonymous-toggle" style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--light-gray)', borderRadius: '0.5rem', border: '2px solid var(--primary)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isAnonymous}
                onChange={(e) => setFormData({ ...formData, isAnonymous: e.target.checked })}
                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600, fontSize: '1rem' }}>Submit Anonymously</span>
            </label>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray)', marginTop: '0.5rem', marginLeft: '2rem' }}>
              Your identity will be kept confidential
            </p>
          </div>

          {/* Resident Information - Hidden if Anonymous */}
          {!formData.isAnonymous && (
            <>
              <h3 style={{ color: 'var(--primary)', marginBottom: '1rem' }}>Resident Information</h3>
              
              <div className="form-group">
                <label className="form-label required">Resident Full Name</label>
                <input 
                  type="text" 
                  name="residentFullName" 
                  className="form-input" 
                  value={formData.residentFullName} 
                  onChange={handleChange} 
                  required={!formData.isAnonymous}
                  placeholder="Enter resident full name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Guardian / Parent Name (Optional)</label>
                <input 
                  type="text" 
                  name="guardianParentName" 
                  className="form-input" 
                  value={formData.guardianParentName} 
                  onChange={handleChange}
                  placeholder="Enter guardian or parent name"
                />
              </div>
            </>
          )}

          {/* Feedback Type */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Feedback Details</h3>

          <div className="form-group">
            <label className="form-label required">Type of Feedback</label>
            <select name="feedbackType" className="form-select" value={formData.feedbackType} onChange={handleChange} required>
              <option value="">Select feedback type</option>
              <option value="Appreciation">Appreciation</option>
              <option value="Suggestion">Suggestion</option>
              <option value="Complaint">Complaint</option>
              <option value="Concern">Concern</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Experience Rating */}
          <div className="form-group">
            <label className="form-label required">Overall Experience Rating</label>
            <div className="radio-group">
              {['Very Poor', 'Poor', 'Average', 'Good', 'Excellent'].map((rating) => (
                <label key={rating} className={`radio-option ${formData.experienceRating === rating ? 'selected' : ''}`}>
                  <input 
                    type="radio" 
                    name="experienceRating" 
                    value={rating} 
                    checked={formData.experienceRating === rating} 
                    onChange={handleChange}
                    required
                  />
                  {rating}
                </label>
              ))}
            </div>
          </div>

          {/* Service Area */}
          <div className="form-group">
            <label className="form-label">Related Service Area (Optional)</label>
            <select name="serviceArea" className="form-select" value={formData.serviceArea} onChange={handleChange}>
              <option value="">Select service area</option>
              <option value="Care & Support">Care & Support</option>
              <option value="Staff Behavior">Staff Behavior</option>
              <option value="Cleanliness">Cleanliness</option>
              <option value="Food & Nutrition">Food & Nutrition</option>
              <option value="Safety & Security">Safety & Security</option>
              <option value="Activities & Engagement">Activities & Engagement</option>
              <option value="Medical Support">Medical Support</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Feedback Details */}
          <div className="form-group">
            <label className="form-label required">Feedback / Suggestion Details</label>
            <textarea 
              name="feedbackDetails" 
              className="form-textarea" 
              value={formData.feedbackDetails} 
              onChange={handleChange} 
              placeholder="Please share your experience, concern, or suggestion so we can improve our services..."
              required
              maxLength={2000}
              style={{ minHeight: '150px' }}
            />
            <small style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
              {formData.feedbackDetails.length} / 2000 characters
            </small>
          </div>

          {/* Digital Signature */}
          <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1rem' }}>Digital Signature</h3>
          
          <div className="form-group">
            <label className="form-label required">Signature</label>
            <div className="signature-container">
              <SignatureCanvas
                ref={signaturePad}
                canvasProps={{ className: 'signature-canvas' }}
              />
              <div className="signature-controls">
                <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>Sign above</span>
                <button type="button" onClick={clearSignature} className="btn btn-secondary btn-sm">Clear</button>
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="form-group">
            <div className="checkbox-group">
              <input type="checkbox" id="consent" required />
              <label htmlFor="consent">
                I confirm this feedback is genuine and shared to help improve services.
              </label>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '1rem' }}>
            Submit Feedback
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResidentFeedbackForm;